import { useState } from "react";
import "./QuizEditor.css";

const EMPTY_Q = () => ({ question: "", options: ["", "", "", ""], correct: 0 });

export default function QuizEditor({ classId, initial, onSave, onCancel }) {
  const [title,     setTitle]     = useState(initial?.title || "Cuestionario");
  const [deadline,  setDeadline]  = useState(
    initial?.deadline ? initial.deadline.slice(0, 16) : ""
  );
  const [questions, setQuestions] = useState(
    initial?.questions?.length ? initial.questions.map((q) => ({
      question: q.question,
      options:  Array.isArray(q.options) ? q.options : JSON.parse(q.options),
      correct:  q.correct,
    })) : [EMPTY_Q()]
  );
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const updateQ = (i, field, val) => {
    const copy = [...questions];
    copy[i] = { ...copy[i], [field]: val };
    setQuestions(copy);
  };

  const updateOption = (qi, oi, val) => {
    const copy = [...questions];
    const opts = [...copy[qi].options];
    opts[oi] = val;
    copy[qi] = { ...copy[qi], options: opts };
    setQuestions(copy);
  };

  const addQuestion    = () => setQuestions([...questions, EMPTY_Q()]);
  const removeQuestion = (i) => setQuestions(questions.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    for (const q of questions) {
      if (!q.question.trim()) { setError("Todas las preguntas deben tener texto"); return; }
      if (q.options.some((o) => !o.trim())) { setError("Todas las opciones deben tener texto"); return; }
    }
    setSaving(true);
    try {
      await onSave({
        class_id:  classId,
        title,
        deadline:  deadline ? new Date(deadline).toISOString() : null,
        questions,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Estado visual del deadline
  const deadlineStatus = (() => {
    if (!deadline) return null;
    const diff = new Date(deadline) - new Date();
    if (diff <= 0) return { label: "Ya cerrado", color: "#c0392b", bg: "#fde8e8" };
    const hrs = Math.floor(diff / 36e5);
    if (hrs < 24) return { label: `Cierra en ${hrs}h ${Math.floor((diff % 36e5) / 60000)}min`, color: "#e67e22", bg: "#fef3e7" };
    return { label: `Cierra en ${Math.floor(hrs / 24)}d`, color: "#27ae60", bg: "#e8f9ee" };
  })();

  return (
    <div className="qe-wrap">
      <div className="qe-header">
        <input
          className="qe-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título del cuestionario"
        />
        <p className="qe-subtitle">Selecciona la respuesta correcta con el círculo de cada pregunta</p>
      </div>

      {/* ── Fecha límite ── */}
      <div style={{
        padding: ".9rem 1.8rem",
        background: "var(--white)",
        borderBottom: "0.5px solid var(--border)",
        display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: ".3rem", flex: 1, minWidth: 200 }}>
          <label style={{
            fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.72rem",
            color: "var(--teal-dark)", textTransform: "uppercase", letterSpacing: .5,
          }}>
            Fecha y hora límite de entrega
          </label>
          <input
            type="datetime-local"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            style={{
              fontFamily: "var(--font-body)", fontSize: "0.85rem",
              border: "1px solid var(--border)", borderRadius: 6,
              padding: "6px 10px", outline: "none", color: "var(--text)",
              background: "var(--off-white)", maxWidth: 240,
            }}
          />
          <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
            Opcional — si no se establece, el quiz siempre estará disponible
          </span>
        </div>
        {deadlineStatus && (
          <div style={{
            background: deadlineStatus.bg, color: deadlineStatus.color,
            fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.78rem",
            padding: "6px 14px", borderRadius: 20,
            border: `0.5px solid ${deadlineStatus.color}44`,
          }}>
            {deadlineStatus.label}
          </div>
        )}
        {deadline && (
          <button
            type="button"
            onClick={() => setDeadline("")}
            style={{
              background: "none", border: "0.5px solid var(--border)",
              borderRadius: 6, padding: "5px 10px", cursor: "pointer",
              fontFamily: "var(--font-display)", fontWeight: 700,
              fontSize: "0.72rem", color: "var(--text-muted)",
            }}
          >
            Quitar límite
          </button>
        )}
      </div>

      <div className="qe-questions">
        {questions.map((q, qi) => (
          <div className="qe-question-card" key={qi}>
            <div className="qe-question-header">
              <span className="qe-q-num">Pregunta {qi + 1}</span>
              {questions.length > 1 && (
                <button className="qe-remove" type="button" onClick={() => removeQuestion(qi)}>✕</button>
              )}
            </div>

            <input
              className="qe-q-input"
              value={q.question}
              onChange={(e) => updateQ(qi, "question", e.target.value)}
              placeholder="Escribe la pregunta aquí..."
            />

            <div className="qe-options">
              {q.options.map((opt, oi) => (
                <label className={`qe-option ${q.correct === oi ? "correct" : ""}`} key={oi}>
                  <input
                    type="radio"
                    name={`correct-${qi}`}
                    checked={q.correct === oi}
                    onChange={() => updateQ(qi, "correct", oi)}
                  />
                  <span className="qe-option-letter">{["A","B","C","D"][oi]}</span>
                  <input
                    className="qe-option-input"
                    value={opt}
                    onChange={(e) => updateOption(qi, oi, e.target.value)}
                    placeholder={`Opción ${["A","B","C","D"][oi]}`}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button className="qe-add-btn" type="button" onClick={addQuestion}>
        + Agregar pregunta
      </button>

      {error && <p style={{ color: "#c0392b", fontSize: "0.82rem", margin: ".5rem 1.4rem" }}>{error}</p>}

      <div className="qe-footer">
        <button className="btn-outline" type="button" onClick={onCancel}>Cancelar</button>
        <button className="btn-gold"    type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Guardando..." : "Guardar cuestionario"}
        </button>
      </div>
    </div>
  );
}